import { Globe, Scale, Landmark, Building2, ShieldCheck } from "lucide-react";
import { useFlowReveal } from "@/hooks/useFlowReveal";

const standards = [
  {
    icon: Globe,
    title: "Web Content Accessibility Guidelines",
    abbr: "WCAG",
    tag: "Universal",
    description: "Global standard for accessible digital content",
  },
  {
    icon: Scale,
    title: "Americans with Disabilities Act",
    abbr: "ADA",
    tag: "Civil Rights",
    description: "Ensures equal access for people with disabilities in the U.S.",
  },
  {
    icon: Landmark,
    title: "Section 508 of the Rehabilitation Act",
    abbr: null,
    tag: "Federal",
    description: "Mandates accessibility for U.S. government digital systems",
  },
  {
    icon: Building2,
    title: "Accessible Canada Act",
    abbr: "ACA",
    tag: "National",
    description: "Drives accessibility across federally regulated sectors in Canada",
  },
  {
    icon: ShieldCheck,
    title: "European Accessibility Act",
    abbr: "EAA",
    tag: "Directive",
    description: "Harmonizes accessibility requirements across the European Union",
  },
];

const GlobalSection = () => {
  const flowRef = useFlowReveal<HTMLDivElement>();

  return (
  <section id="global" className="section-padding" aria-labelledby="global-heading">
    <div className="max-w-7xl mx-auto">
      <p className="eyebrow mb-4">Global</p>
      <h2 id="global-heading" className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
        <abbr title="Americans with Disabilities Act">ADA</abbr> Website Compliance, Section 508 &amp; <abbr title="European Accessibility Act">EAA</abbr> Compliance
      </h2>

      {/* Tag bar */}
      <div className="flex flex-wrap gap-3 mb-8" role="list" aria-label="Global standards" data-tts-skip>
        {standards.map((s) => (
          <span key={s.tag} className="inline-flex items-center gap-2 border bg-card text-primary text-xs font-semibold px-3 py-2 rounded-md min-h-[44px]" role="listitem">
            {s.abbr ? <abbr title={s.title}>{s.abbr}</abbr> : s.title.split(" ")[0]}
            <span className="text-muted-foreground">– {s.tag}</span>
          </span>
        ))}
      </div>

      <div ref={flowRef} className="grid sm:grid-cols-2 lg:grid-cols-6 gap-6">
        {standards.map((s, i) => (
          <article key={s.title} className={`flow-step border-t-2 border-foreground pt-5 ${i >= 3 ? "lg:col-span-3" : "lg:col-span-2"}`} style={{ "--i": Math.min(i, 5) } as React.CSSProperties}>
            <div className="mb-4" aria-hidden="true">
              <s.icon size={20} className="text-primary" />
            </div>
            <h3 className="font-display font-bold text-foreground mb-0.5">
              {s.title} {s.abbr && <>(<abbr title={s.title}>{s.abbr}</abbr>)</>}
            </h3>
            <p className="text-sm text-primary font-medium mb-2">{s.tag}</p>
            <p className="text-base text-muted-foreground leading-relaxed">{s.description}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
  );
};

export default GlobalSection;
